import React, { useState, useEffect, useCallback, useRef } from "react";
import { db, storage } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  query,
  orderBy,
  addDoc,
  where,
  deleteDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faSearch,
  faEnvelope,
  faDownload,
  faRedo,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

// Skeleton Row
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(8)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

export const AdminVolunteer = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const auth = getAuth();
  const isMounted = useRef(true);

  // State utama
  const [state, setState] = useState({
    volunteers: [],
    filteredVolunteers: [],
    searchQuery: "",
    selectedVolunteers: [],
    loading: true,
    totalVolunteers: 0,
    totalFiltered: 0,
    allVolunteers: [],
    emailTemplateId: 1,
    error: null,
  });

  // State tambahan
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [eligibleVolunteerCount, setEligibleVolunteerCount] = useState(0);
  const [isEditPositionModalOpen, setIsEditPositionModalOpen] = useState(false);
  const [editVolunteerId, setEditVolunteerId] = useState(null);
  const [newPosition, setNewPosition] = useState("");

  // State untuk sertifikat massal
  const [isGeneratingCertificates, setIsGeneratingCertificates] =
    useState(false);
  const [certificateProgress, setCertificateProgress] = useState({
    current: 0,
    total: 0,
  });

  // Cleanup
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Format tanggal
  const formatDate = useCallback((date) => {
    if (!date) return "Tidak tersedia";
    try {
      const timestamp =
        date.seconds || date._seconds || date.toDate?.() || date;
      return timestamp
        ? new Date(timestamp * 1000).toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })
        : "Tidak tersedia";
    } catch {
      return "Tidak tersedia";
    }
  }, []);

  // Status badge
  const getStatusProps = (status) => {
    switch (status) {
      case true:
        return { text: "Disetujui", color: "text-lime-600" };
      case false:
        return { text: "Tidak Diterima", color: "text-red-600" };
      case null:
        return { text: "Menunggu Review", color: "text-yellow-600" };
      default:
        return { text: "Tidak Diketahui", color: "text-gray-600" };
    }
  };

  // Cek admin
  const checkAdminStatus = useCallback(async () => {
    if (!user?.uid) {
      setState((prev) => ({
        ...prev,
        error: "Pengguna tidak terautentikasi.",
      }));
      navigate("/login");
      showNotification("error", "Silakan login untuk mengakses halaman admin.");
      return false;
    }

    try {
      const token = await user.getIdTokenResult(true);
      const userDoc = await getDoc(doc(db, "users", user.uid));

      if (
        userDoc.exists() &&
        userDoc.data().role === "admin" &&
        token.claims.role === "admin"
      ) {
        setIsAdmin(true);
        return true;
      } else {
        setState((prev) => ({
          ...prev,
          error: "Akses ditolak. Hanya admin yang diizinkan.",
        }));
        showNotification(
          "error",
          "Akses ditolak. Hubungi administrator sistem."
        );
        navigate("/MyRegistration");
        return false;
      }
    } catch (err) {
      console.error("[ERROR] Gagal memeriksa status admin:", err);
      setState((prev) => ({ ...prev, error: "Gagal memeriksa status admin." }));
      showNotification("error", "Gagal memeriksa status admin.");
      navigate("/login");
      return false;
    }
  }, [user, navigate, showNotification]);

  // Fetch volunteers
  const fetchVolunteers = useCallback(async () => {
    if (!isMounted.current) return;
    setState((prev) => ({
      ...prev,
      loading: true,
      volunteers: [],
      filteredVolunteers: [],
    }));

    try {
      const q = query(
        collection(db, "pendaftaranVolunteers"),
        orderBy("namaLengkap")
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        sentTemplateIds: d.data().sentTemplateIds || [],
      }));

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          volunteers: list,
          filteredVolunteers: prev.searchQuery ? prev.filteredVolunteers : list,
          loading: false,
          totalVolunteers: list.length,
          totalFiltered: prev.searchQuery ? prev.totalFiltered : list.length,
        }));
      }
    } catch (err) {
      console.error("Gagal memuat data volunteer:", err);
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "Gagal memuat data volunteer.",
        }));
        showNotification("error", "Gagal memuat data volunteer.");
      }
    }
  }, [showNotification]);

  const fetchAllVolunteers = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "pendaftaranVolunteers"));
      const all = await Promise.all(
        snap.docs.map(async (d) => {
          const data = {
            id: d.id,
            ...d.data(),
            sentTemplateIds: d.data().sentTemplateIds || [],
          };
          if (data.email && isValidEmail(data.email)) return data;
          if (data.uid) {
            const email = await fetchUserEmail(data.uid);
            if (email && isValidEmail(email)) {
              try {
                await updateDoc(doc(db, "pendaftaranVolunteers", data.id), {
                  email,
                });
                return { ...data, email };
              } catch {}
            }
          }
          return data;
        })
      );

      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          allVolunteers: all,
          totalVolunteers: snap.size,
          totalFiltered: prev.searchQuery ? prev.totalFiltered : snap.size,
        }));
      }
    } catch (err) {
      console.error("Gagal memuat semua volunteer:", err);
      showNotification("error", "Gagal memuat semua data volunteer.");
    }
  }, [showNotification]);

  const fetchUserEmail = async (uid) => {
    try {
      const docSnap = await getDoc(doc(db, "users", uid));
      return docSnap.exists() ? docSnap.data().email || null : null;
    } catch {
      return null;
    }
  };

  // GENERATE SERTIFIKAT SATU PER SATU
  const generateVolunteerCertificate = async (volunteer) => {
    console.log(`[CERT] Membuat sertifikat untuk ${volunteer.namaLengkap}`);

    try {
      const token = await auth.currentUser.getIdTokenResult(true);
      if (token.claims.role !== "admin") throw new Error("Hanya admin!");

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = "/certificate.png";

      await new Promise((res, rej) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          res();
        };
        img.onerror = () =>
          rej(new Error("Template certificate.png tidak ditemukan"));
      });

      await document.fonts.ready;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Nama besar
      ctx.font = "260px 'Alex Brush'";
      ctx.fillStyle = "#5F7C17";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(volunteer.namaLengkap || "Unknown", centerX, centerY + 50);

      // Posisi
      ctx.font = "48px 'Poppins'";
      ctx.fillStyle = "#5F7C17";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(
        volunteer.posisiVolunteer || "Relawan",
        centerX,
        centerY + 320
      );

      // Hapus sertifikat lama
      const q = query(
        collection(db, "sertifikatVolunteers"),
        where("volunteerId", "==", volunteer.id)
      );
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        const oldRef = ref(
          storage,
          `sertifikatVolunteers/${volunteer.id}/${d.data().fileName}`
        );
        try {
          await deleteObject(oldRef);
        } catch (e) {
          if (e.code !== "storage/object-not-found") throw e;
        }
        await deleteDoc(d.ref);
      }

      // Upload baru
      const fileName = `Sertifikat_${(volunteer.namaLengkap || "Volunteer")
        .replace(/\s+/g, "_")
        .toLowerCase()}_${volunteer.id}.png`;
      const storageRef = ref(
        storage,
        `sertifikatVolunteers/${volunteer.id}/${fileName}`
      );
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      await uploadBytes(storageRef, blob);
      const downloadURL = await getDownloadURL(storageRef);

      // Simpan metadata
      await addDoc(collection(db, "sertifikatVolunteers"), {
        volunteerId: volunteer.id,
        namaLengkap: volunteer.namaLengkap,
        posisiVolunteer: volunteer.posisiVolunteer,
        fileName,
        downloadURL,
        createdAt: new Date(),
      });

      await updateDoc(doc(db, "pendaftaranVolunteers", volunteer.id), {
        linkSertifikat: downloadURL,
      });

      // Update UI
      setState((prev) => ({
        ...prev,
        volunteers: prev.volunteers.map((v) =>
          v.id === volunteer.id ? { ...v, linkSertifikat: downloadURL } : v
        ),
        filteredVolunteers: prev.filteredVolunteers.map((v) =>
          v.id === volunteer.id ? { ...v, linkSertifikat: downloadURL } : v
        ),
        allVolunteers: prev.allVolunteers.map((v) =>
          v.id === volunteer.id ? { ...v, linkSertifikat: downloadURL } : v
        ),
      }));

      showNotification(
        "success",
        `Sertifikat ${volunteer.namaLengkap} berhasil dibuat!`
      );
      return true;
    } catch (err) {
      console.error("[CERT ERROR]", err);
      showNotification(
        "error",
        `Gagal buat sertifikat ${volunteer.namaLengkap}: ${err.message}`
      );
      return false;
    }
  };

  // FITUR BARU: BUAT SERTIFIKAT MASSAL
  const handleGenerateAllCertificates = async () => {
    const selectedApproved = state.allVolunteers.filter(
      (v) => state.selectedVolunteers.includes(v.id) && v.status === true
    );

    if (selectedApproved.length === 0) {
      showNotification("error", "Tidak ada volunteer disetujui yang dipilih.");
      return;
    }

    setIsGeneratingCertificates(true);
    setCertificateProgress({ current: 0, total: selectedApproved.length });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < selectedApproved.length; i++) {
      const volunteer = selectedApproved[i];
      const result = await generateVolunteerCertificate(volunteer);
      if (result) success++;
      else failed++;

      setCertificateProgress((prev) => ({ ...prev, current: i + 1 }));
      await new Promise((r) => setTimeout(r, 400)); // Delay biar tidak kena rate limit
    }

    setIsGeneratingCertificates(false);
    setCertificateProgress({ current: 0, total: 0 });

    showNotification(
      "success",
      `Selesai! Berhasil: ${success}, Gagal: ${failed} dari ${selectedApproved.length} volunteer.`
    );
  };

  // Reset status
  const handleResetStatus = async (volunteerId) => {
    setUpdatingStatus((prev) => ({ ...prev, [volunteerId]: true }));
    try {
      await updateDoc(doc(db, "pendaftaranVolunteers", volunteerId), {
        status: null,
      });
      setState((prev) => ({
        ...prev,
        volunteers: prev.volunteers.map((v) =>
          v.id === volunteerId ? { ...v, status: null } : v
        ),
        filteredVolunteers: prev.filteredVolunteers.map((v) =>
          v.id === volunteerId ? { ...v, status: null } : v
        ),
        allVolunteers: prev.allVolunteers.map((v) =>
          v.id === volunteerId ? { ...v, status: null } : v
        ),
        selectedVolunteers: prev.selectedVolunteers.filter(
          (id) => id !== volunteerId
        ),
      }));
      showNotification("success", "Status direset ke Menunggu Review.");
    } catch (err) {
      showNotification("error", "Gagal mereset status.");
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [volunteerId]: false }));
    }
  };

  // Edit posisi
  const handleEditPosition = async (e) => {
    e.preventDefault();
    if (!newPosition.trim()) {
      showNotification("error", "Posisi tidak boleh kosong.");
      return;
    }

    try {
      await updateDoc(doc(db, "pendaftaranVolunteers", editVolunteerId), {
        posisiVolunteer: newPosition.trim(),
      });
      setState((prev) => ({
        ...prev,
        volunteers: prev.volunteers.map((v) =>
          v.id === editVolunteerId
            ? { ...v, posisiVolunteer: newPosition.trim() }
            : v
        ),
        filteredVolunteers: prev.filteredVolunteers.map((v) =>
          v.id === editVolunteerId
            ? { ...v, posisiVolunteer: newPosition.trim() }
            : v
        ),
        allVolunteers: prev.allVolunteers.map((v) =>
          v.id === editVolunteerId
            ? { ...v, posisiVolunteer: newPosition.trim() }
            : v
        ),
      }));
      showNotification("success", "Posisi berhasil diperbarui.");
      setIsEditPositionModalOpen(false);
      setNewPosition("");
      setEditVolunteerId(null);
    } catch {
      showNotification("error", "Gagal memperbarui posisi.");
    }
  };

  // Inisialisasi
  useEffect(() => {
    const init = async () => {
      const adminOk = await checkAdminStatus();
      if (adminOk) {
        await Promise.all([fetchVolunteers(), fetchAllVolunteers()]);
      }
    };
    init();
  }, [checkAdminStatus, fetchVolunteers, fetchAllVolunteers]);

  // Pencarian
  useEffect(() => {
    if (state.searchQuery) {
      const filtered = state.allVolunteers.filter((v) =>
        [v.namaLengkap, v.posisiVolunteer, v.id, v.sentTemplateIds?.join(", ")]
          .filter(Boolean)
          .some((f) =>
            f.toString().toLowerCase().includes(state.searchQuery.toLowerCase())
          )
      );
      setState((prev) => ({
        ...prev,
        filteredVolunteers: filtered,
        totalFiltered: filtered.length,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        filteredVolunteers: prev.volunteers,
        totalFiltered: prev.totalVolunteers,
      }));
    }
  }, [state.searchQuery, state.allVolunteers, state.volunteers]);

  // Hitung eligible email
  useEffect(() => {
    const calc = async () => {
      if (!isEmailModalOpen || state.selectedVolunteers.length === 0) {
        setEligibleVolunteerCount(0);
        return;
      }
      let count = 0;
      for (const v of state.allVolunteers.filter(
        (x) =>
          state.selectedVolunteers.includes(x.id) &&
          x.status === true &&
          (!x.sentTemplateIds ||
            !x.sentTemplateIds.includes(state.emailTemplateId.toString()))
      )) {
        if (v.email && isValidEmail(v.email)) count++;
        else if (v.uid) {
          const email = await fetchUserEmail(v.uid);
          if (email && isValidEmail(email)) {
            try {
              await updateDoc(doc(db, "pendaftaranVolunteers", v.id), {
                email,
              });
              count++;
            } catch {}
          }
        }
      }
      setEligibleVolunteerCount(count);
    };
    calc();
  }, [state.selectedVolunteers, isEmailModalOpen, state.emailTemplateId]);

  // Checkbox handler
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setState((prev) => ({
        ...prev,
        selectedVolunteers: prev.filteredVolunteers
          .filter((v) => v.status === true)
          .map((v) => v.id),
      }));
    } else {
      setState((prev) => ({ ...prev, selectedVolunteers: [] }));
    }
  };

  const handleSelectVolunteer = (id) => {
    setState((prev) => ({
      ...prev,
      selectedVolunteers: prev.selectedVolunteers.includes(id)
        ? prev.selectedVolunteers.filter((x) => x !== id)
        : [...prev.selectedVolunteers, id],
    }));
  };

  // Update status
  const handleUpdateStatus = async (id, status) => {
    setUpdatingStatus((prev) => ({ ...prev, [id]: true }));
    try {
      await updateDoc(doc(db, "pendaftaranVolunteers", id), { status });
      setState((prev) => ({
        ...prev,
        volunteers: prev.volunteers.map((v) =>
          v.id === id ? { ...v, status } : v
        ),
        filteredVolunteers: prev.filteredVolunteers.map((v) =>
          v.id === id ? { ...v, status } : v
        ),
        allVolunteers: prev.allVolunteers.map((v) =>
          v.id === id ? { ...v, status } : v
        ),
        selectedVolunteers:
          status === false
            ? prev.selectedVolunteers.filter((x) => x !== id)
            : prev.selectedVolunteers,
      }));
    } catch {
      showNotification("error", "Gagal update status.");
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (state.selectedVolunteers.length === 0) {
      showNotification("error", "Pilih minimal 1 volunteer.");
      return;
    }

    const data = state.allVolunteers.filter((v) =>
      state.selectedVolunteers.includes(v.id)
    );
    const headers = [
      "ID,Nama Lengkap,Email,Telepon,Posisi Volunteer,Tanggal Pendaftaran,Status,Template Email Terkirim,Nama Depan,Nama Belakang,Nomor Identitas,Link Sertifikat,Username Instagram,Tanggal Lahir,Gender,Negara,Provinsi,Kota,Alamat,Ukuran Jersey,Motivasi dan Pengalaman",
    ];

    const rows = data.map((v) =>
      [
        v.id,
        v.namaLengkap,
        v.email,
        v.telepon,
        v.posisiVolunteer,
        formatDate(v.createdAt),
        getStatusProps(v.status).text,
        v.sentTemplateIds?.join(" ") || "-",
        v.namaDepan,
        v.namaBelakang,
        v.nomorIdentitas,
        v.linkSertifikat || "",
        v.usernameInstagram,
        v.tanggalLahir,
        v.gender,
        v.negara,
        v.provinsi,
        v.kota,
        v.alamat,
        v.ukuranJersey,
        v.motivasiDanPengalaman,
      ]
        .map((f) => `"${(f || "").toString().replace(/"/g, '""')}"`)
        .join(",")
    );

    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `volunteers_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification("success", "CSV berhasil diekspor.");
  };

  const isValidEmail = (email) =>
    email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const delay = (ms) => new Promise((r) => setTimeout(r, ms));

  const sendBrevoTemplateViaBackend = async ({ to, name, templateId }) => {
    const token = await user?.getIdToken?.();
    if (!token) throw new Error("Not authenticated");

    const url = `${API_BASE_URL}/api/send-brevo-template`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        to,
        name,
        templateId,
        eventName: "JOTR",
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || "Failed to send email");
    }
    return data;
  };

  // Kirim email
  const sendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);

    try {
      const templateId = state.emailTemplateId;
      const eligible = state.allVolunteers.filter(
        (v) =>
          state.selectedVolunteers.includes(v.id) &&
          v.status === true &&
          isValidEmail(v.email) &&
          (!v.sentTemplateIds ||
            !v.sentTemplateIds.includes(templateId.toString()))
      );

      if (eligible.length === 0) {
        showNotification("error", "Tidak ada penerima email yang valid.");
        setIsSendingEmail(false);
        return;
      }

      let sent = 0;
      for (const v of eligible) {
        try {
          await sendBrevoTemplateViaBackend({
            to: v.email.trim(),
            name: v.namaLengkap,
            templateId,
          });

          await updateDoc(doc(db, "pendaftaranVolunteers", v.id), {
            emailSent: true,
            emailSentAt: new Date(),
            sentTemplateIds: [
              ...(v.sentTemplateIds || []),
              templateId.toString(),
            ],
          });

          setState((prev) => ({
            ...prev,
            allVolunteers: prev.allVolunteers.map((x) =>
              x.id === v.id
                ? {
                    ...x,
                    emailSent: true,
                    sentTemplateIds: [
                      ...(x.sentTemplateIds || []),
                      templateId.toString(),
                    ],
                  }
                : x
            ),
          }));

          sent++;
          showNotification(
            "info",
            `Terkirim: ${v.email} (${sent}/${eligible.length})`
          );
          await delay(120);
        } catch (err) {
          console.error("Gagal kirim ke", v.email, err);
        }
      }

      showNotification(
        "success",
        `Email terkirim ke ${sent} dari ${eligible.length} penerima.`
      );
    } catch (err) {
      showNotification("error", "Gagal mengirim email.");
    } finally {
      setIsSendingEmail(false);
      setIsEmailModalOpen(false);
    }
  };

  const handleSearchChange = (e) =>
    setState((prev) => ({ ...prev, searchQuery: e.target.value }));

  const closeModal = () => setSelectedVolunteer(null);

  const VolunteerModal = ({ volunteer }) => (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 py-20 sm:p-4 transition-opacity duration-300"
      onClick={closeModal}
    >
      <div
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mx-2 w-11/12 sm:max-w-lg md:max-w-2xl lg:max-w-4xl min-h-[50vh] max-h-[70vh] sm:max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Detail Pendaftaran Volunteer
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-base sm:text-sm text-gray-600">
          <p>
            <strong>ID:</strong> {volunteer.id || "Tidak tersedia"}
          </p>
          <p>
            <strong>Nama Depan:</strong>{" "}
            {volunteer.namaDepan || "Tidak tersedia"}
          </p>
          <p>
            <strong>Nama Belakang:</strong>{" "}
            {volunteer.namaBelakang || "Tidak tersedia"}
          </p>
          <p>
            <strong>Nama Lengkap:</strong>{" "}
            {volunteer.namaLengkap || "Tidak tersedia"}
          </p>
          <p>
            <strong>Email:</strong> {volunteer.email || "Tidak tersedia"}
          </p>
          <p>
            <strong>Telepon:</strong> {volunteer.telepon || "Tidak tersedia"}
          </p>
          <p>
            <strong>Nomor Identitas:</strong>{" "}
            {volunteer.nomorIdentitas || "Tidak tersedia"}
          </p>
          <p>
            <strong>Link Sertifikat:</strong>{" "}
            {volunteer.linkSertifikat ? (
              <a
                href={volunteer.linkSertifikat}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Buka Sertifikat
              </a>
            ) : (
              "Tidak tersedia"
            )}
          </p>
          <p>
            <strong>Username Instagram:</strong>{" "}
            {volunteer.usernameInstagram || "Tidak tersedia"}
          </p>
          <p>
            <strong>Tanggal Lahir:</strong>{" "}
            {volunteer.tanggalLahir || "Tidak tersedia"}
          </p>
          <p>
            <strong>Gender:</strong> {volunteer.gender || "Tidak tersedia"}
          </p>
          <p>
            <strong>Negara:</strong> {volunteer.negara || "Tidak tersedia"}
          </p>
          <p>
            <strong>Provinsi:</strong> {volunteer.provinsi || "Tidak tersedia"}
          </p>
          <p>
            <strong>Kota:</strong> {volunteer.kota || "Tidak tersedia"}
          </p>
          <p>
            <strong>Alamat:</strong> {volunteer.alamat || "Tidak tersedia"}
          </p>
          <p>
            <strong>Ukuran Jersey:</strong>{" "}
            {volunteer.ukuranJersey || "Tidak tersedia"}
          </p>
          <p>
            <strong>Posisi Volunteer:</strong>{" "}
            {volunteer.posisiVolunteer || "Tidak tersedia"}
          </p>
          <p>
            <strong>Tanggal Pendaftaran:</strong>{" "}
            {formatDate(volunteer.createdAt)}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            <span className={getStatusProps(volunteer.status).color}>
              {getStatusProps(volunteer.status).text}
            </span>
          </p>
          <p>
            <strong>Template Email Terkirim:</strong>{" "}
            {volunteer.sentTemplateIds?.length > 0
              ? volunteer.sentTemplateIds.join(", ")
              : "Tidak tersedia"}
          </p>
          <p>
            <strong>Motivasi dan Pengalaman:</strong>{" "}
            {volunteer.motivasiDanPengalaman || "Tidak tersedia"}
          </p>
        </div>
        <div className="flex justify-center mt-6">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-colors duration-200 w-full max-w-[8rem]"
            disabled={state.loading}
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <section className="min-h-screen bg-white px-4 py-20 flex items-center justify-center">
        <div className="max-w-7xl mx-auto text-center">
          {state.error && (
            <div className="bg-red-100 text-red-800 p-4 rounded-xl mb-6 inline-flex items-center">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="h-5 w-5 mr-2"
              />
              <span>{state.error}</span>
            </div>
          )}
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2 bg-lime-600 text-white rounded-xl hover:bg-lime-700 transition-colors duration-200"
          >
            Kembali ke Beranda
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-white px-4 py-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative w-full sm:max-w-xl">
            <input
              type="text"
              value={state.searchQuery}
              onChange={handleSearchChange}
              placeholder="Cari berdasarkan nama, posisi, atau ID template..."
              className="input w-full p-2 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 text-slate-800"
            />
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setIsEmailModalOpen(true)}
              disabled={state.selectedVolunteers.length === 0 || isSendingEmail}
              className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
                state.selectedVolunteers.length === 0 || isSendingEmail
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-cyan-600 text-white hover:bg-cyan-800"
              }`}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              Kirim Email
            </button>

            <button
              onClick={handleExportCSV}
              disabled={state.selectedVolunteers.length === 0}
              className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
                state.selectedVolunteers.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-800"
              }`}
            >
              <FontAwesomeIcon icon={faDownload} />
              Export CSV
            </button>

            {/* TOMBOL BARU: SERTIFIKAT MASSAL */}
            <button
              onClick={handleGenerateAllCertificates}
              disabled={
                isGeneratingCertificates ||
                state.selectedVolunteers.length === 0
              }
              className={`px-4 py-2 rounded-xl text-sm sm:text-base flex items-center gap-2 ${
                isGeneratingCertificates ||
                state.selectedVolunteers.length === 0
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }`}
            >
              <FontAwesomeIcon icon={faDownload} />
              {isGeneratingCertificates
                ? `Membuat... (${certificateProgress.current}/${certificateProgress.total})`
                : "Buat Sertifikat Massal"}
            </button>
          </div>
        </div>

        {/* Error */}
        {state.error && (
          <div className="bg-red-100 text-red-800 p-4 rounded-xl mb-6 flex items-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="h-5 w-5 mr-2"
            />
            <span>{state.error}</span>
          </div>
        )}

        {/* Tabel */}
        <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto mb-8">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      state.selectedVolunteers.length > 0 &&
                      state.selectedVolunteers.length ===
                        state.filteredVolunteers.filter(
                          (v) => v.status === true
                        ).length
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nama Lengkap
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Telepon
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Posisi Volunteer
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tanggal Pendaftaran
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template Email Terkirim
                </th>
                <th className="px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.filteredVolunteers.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    {state.searchQuery
                      ? "Tidak ada yang cocok."
                      : "Belum ada data volunteer."}
                  </td>
                </tr>
              ) : (
                state.filteredVolunteers.map((volunteer) => (
                  <tr key={volunteer.id}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={state.selectedVolunteers.includes(
                          volunteer.id
                        )}
                        onChange={() => handleSelectVolunteer(volunteer.id)}
                        disabled={volunteer.status !== true}
                        className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {volunteer.namaLengkap || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {volunteer.telepon || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {volunteer.posisiVolunteer || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(volunteer.createdAt)}
                    </td>
                    <td
                      className={`px-4 py-4 whitespace-nowrap text-sm ${
                        getStatusProps(volunteer.status).color
                      }`}
                    >
                      {getStatusProps(volunteer.status).text}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {volunteer.sentTemplateIds?.length > 0
                        ? volunteer.sentTemplateIds.join(", ")
                        : "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => setSelectedVolunteer(volunteer)}
                        className="text-cyan-600 hover:text-cyan-900"
                        disabled={updatingStatus[volunteer.id]}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(volunteer.id, true)}
                        className="text-green-600 hover:text-green-800"
                        disabled={updatingStatus[volunteer.id]}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(volunteer.id, false)}
                        className="text-red-600 hover:text-red-900"
                        disabled={updatingStatus[volunteer.id]}
                      >
                        <FontAwesomeIcon icon={faTimesCircle} />
                      </button>
                      <button
                        onClick={() => handleResetStatus(volunteer.id)}
                        className="text-yellow-600 hover:text-yellow-800"
                        disabled={updatingStatus[volunteer.id]}
                      >
                        <FontAwesomeIcon icon={faRedo} />
                      </button>
                      <button
                        onClick={() => {
                          setEditVolunteerId(volunteer.id);
                          setNewPosition(volunteer.posisiVolunteer || "");
                          setIsEditPositionModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={updatingStatus[volunteer.id]}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => generateVolunteerCertificate(volunteer)}
                        className="text-purple-600 hover:text-purple-800"
                        disabled={
                          updatingStatus[volunteer.id] ||
                          volunteer.status !== true
                        }
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end items-center px-4 py-2">
          <span className="text-sm text-gray-600 mr-4">
            {state.filteredVolunteers.length} dari {state.totalFiltered}{" "}
            Volunteer
          </span>
        </div>

        {/* MODAL DETAIL */}
        {selectedVolunteer && <VolunteerModal volunteer={selectedVolunteer} />}

        {/* MODAL EMAIL */}
        {isEmailModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl py-6 px-4 max-w-lg w-11/12 mx-2">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Kirim Email ke Volunteer
              </h2>
              <form onSubmit={sendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Template ID (Brevo)
                  </label>
                  <input
                    type="number"
                    value={state.emailTemplateId}
                    onChange={(e) =>
                      setState((prev) => ({
                        ...prev,
                        emailTemplateId: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 text-slate-800 text-sm"
                    required
                    disabled={isSendingEmail}
                  />
                </div>
                <div className="mb-4 text-sm text-gray-600">
                  <p>
                    Email akan dikirim ke {eligibleVolunteerCount} volunteer
                    yang belum menerima template ID {state.emailTemplateId}.
                  </p>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => setIsEmailModalOpen(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                    disabled={isSendingEmail}
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 rounded-xl text-white ${
                      isSendingEmail
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-cyan-600 hover:bg-cyan-700"
                    }`}
                    disabled={isSendingEmail}
                  >
                    {isSendingEmail ? "Mengirim..." : "Kirim Email"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* MODAL EDIT POSISI */}
        {isEditPositionModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl py-6 px-4 max-w-lg w-11/12 mx-2">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Edit Posisi Volunteer
              </h2>
              <form onSubmit={handleEditPosition}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Posisi Volunteer
                  </label>
                  <input
                    type="text"
                    value={newPosition}
                    onChange={(e) => setNewPosition(e.target.value)}
                    className="input w-full p-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-600 text-slate-800 text-sm"
                    required
                  />
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditPositionModalOpen(false);
                      setNewPosition("");
                      setEditVolunteerId(null);
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700"
                  >
                    Simpan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
